import type { Rename, Replace } from '../typeHelpers.no-deps'
import { isArray } from 'lodash'
import { dictMap } from './dictionary.no-deps'

export function isObject(obj: unknown): obj is Record<string, unknown> {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj)
}

/**
 * Gets the value of an object's field:
 *
 *    const coords = { x: 56, y: 292 }
 *    field('x')(coords) // 56
 */
export function field<T, K extends keyof T>(key: K) {
    return (obj: T): T[K] => obj[key]
}

/**
 * Allows you to bind a method to it's object so that is safe to pass around
 * as a function e.g.
 *
 *     const findInArray = detachMethod([1, 2, 4, 6], 'find')
 *     return findInArray(isEven) // 2
 */
export function detachMethod<T extends {}, K extends keyof T>(obj: T, key: K): T[K] {
    const fn = obj[key]
    if (!(fn instanceof Function)) return fn
    return fn.bind(obj)
}

/**
 * Used to access a field of an unknown entity.
 * Use with caution as field might be optional, or have a different type
 *
 *    function getErrorMessage(error: Error | string) {
 *        return hasField(error, 'message') ? error.message : error
 *    }
 */
export function hasField<K extends string>(obj: unknown, key: K): obj is { [k in K]: unknown } {
    return obj instanceof Object && key in obj
}

export function setField<T, K extends keyof T>(obj: T, key: K, fn: (value: T[K]) => T[K]): T {
    const value = obj[key]
    const newValue = fn(value)

    // If unchanged keep the same object id
    if (value === newValue) return obj

    return { ...obj, [key]: newValue }
}

export function mapField<K extends string, A, B>(key: K, fn: (value: A) => B) {
    return <T extends { [k in K]: A }>(obj: T): Replace<T, { [k in K]: B }> => ({
        ...obj,
        [key]: fn(obj[key]),
    })
}

export function addField<T, K extends string, V>(obj: T, key: K, value: V): T & { [k in K]: V } {
    return { ...obj, [key]: value } as T & { [k in K]: V }
}

export function omit<T, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> {
    obj = { ...obj }
    for (const key of keys) {
        delete obj[key]
    }
    return obj
}

export function rename<T, K extends keyof T, K2 extends string>(obj: T, oldKey: K, newKey: K2): Rename<T, K, K2> {
    return { ...omit(obj, oldKey), [newKey]: obj[oldKey] } as Rename<T, K, K2>
}

export function getKeys<T extends object>(obj: T): Array<keyof T> {
    return Object.keys(obj) as Array<keyof T>
}

/**
 * Compare the values of two objects and return a partial with just the values that are different with ===
 */
export function getObjectsDifference<T extends {}>(original: T, updated: T): Partial<T> {
    const partial: Partial<T> = {}

    const keys = new Set<keyof T>([...getKeys(original), ...getKeys(updated)])

    for (const key of keys) {
        if (original[key] === updated[key]) continue
        partial[key] = updated[key]
    }

    return partial
}

export function removeFalseyValues<T>(obj: T): Partial<T> {
    obj = { ...obj }

    for (const key in obj) {
        if (!obj[key]) delete obj[key]
    }

    return obj
}

export function deepMap(obj: unknown, fn: (value: unknown) => unknown) {
    obj = fn(obj)

    if (isObject(obj)) {
        obj = dictMap(obj, (value) => deepMap(value, fn))
    }

    if (isArray(obj)) {
        obj = obj.map((member) => deepMap(member, fn))
    }
    return obj
}

export function removeFieldsWithUndefinedValues<T>(obj: T): Partial<T> {
    obj = { ...obj }

    for (const key in obj) {
        if (obj[key] === undefined) delete obj[key]
    }

    return obj
}

export function omitIfTrue<T, K extends keyof T>(
    obj: T,
    keys: K[],
    condition: boolean
): Omit<T, K> & Partial<Pick<T, K>> {
    if (!condition) return obj
    return omit(obj, ...keys) as Omit<T, K> & Partial<Pick<T, K>>
}
